import { apiSdk } from '@/api';
import { isAxiosError } from '@/api/axios-error';
import {
  getLoginUser,
  getPageConfig,
  getToken,
  setCurrentTenant,
  setLoginUser,
  setPermission,
  setToken,
} from '@/commons';
import {
  BrandLoader,
  ErrorSystemError,
  ErrorTenantNotFoundPage,
} from '@/components';
import { useEffect, useState } from 'react';
import App from './App';
import {
  createTenantNotFoundError,
  getRootInitErrorType,
  type RootInitErrorType,
} from './root-state';

import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);
dayjs.extend(calendar);

async function initTenant() {
  try {
    const res = await apiSdk.tenant.findCurrent({
      cache: true,
      errorTip: false,
    });
    const tenant = res.data;
    setCurrentTenant(tenant);

    document.title = tenant.name;
    setTabIcon(tenant.logo ?? undefined);
  } catch (error) {
    if (isAxiosError(error)) {
      const statusCode = error.response?.data?.statusCode;
      if (['003001', '003002'].includes(statusCode)) {
        throw createTenantNotFoundError();
      }
    }
  }
}

async function initUser() {
  const res = await apiSdk.users.findCurrent({
    errorTip: false,
  });
  const currentToken = getToken() || getLoginUser()?.accessToken;
  setLoginUser(res.data);
  if (currentToken) setToken(currentToken);
  const permissions = res.data?.permissions || [];
  setPermission(permissions);
}

export default function Root() {
  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState<RootInitErrorType>();

  useEffect(() => {
    (async () => {
      try {
        const pageConfig = getPageConfig();
        if (pageConfig.tenant !== false) {
          await initTenant();
        }

        if (pageConfig.auth === false) {
          setReady(true);
          return;
        }

        await initUser();
        setReady(true);
      } catch (error) {
        const errorType = getRootInitErrorType(error);
        if (errorType) {
          setInitError(errorType);
          return;
        }
        throw error;
      }
    })();
  }, []);

  if (initError === 'tenant-not-found') return <ErrorTenantNotFoundPage />;

  if (initError === 'system-error') return <ErrorSystemError />;

  if (!ready) return <BrandLoader fullscreen />;

  return <App />;
}

function setTabIcon(logo: string = '/logo.svg') {
  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    const ext = logo.split('.').pop()?.toLowerCase();
    link.type = `image/${ext}`;
    document.head.appendChild(link);
  }
  link.href = logo;
}
